import { useEffect } from "react";
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { BookingDetails } from '@/hooks/bookings/types';
import { useQuotes } from '@/hooks/useQuotes';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useRealtimeQuotes } from '@/hooks/bookings/useRealtimeQuotes';
import { BookingDetailsHeader } from './BookingDetailsHeader';
import { BookingDetailsTabs } from './BookingDetailsTabs';
interface BookingDetailsModalProps {
  booking: BookingDetails | null;
  isOpen: boolean;
  onClose: () => void;
  userRoles?: {
    isAdmin: boolean;
    isPlatformAdmin: boolean;
    isOrgAdmin: boolean;
  };
  onBookingUpdate?: (bookingId: string) => Promise<void>;
}
const BookingDetailsModal: React.FC<BookingDetailsModalProps> = ({
  booking,
  isOpen,
  onClose,
  userRoles,
  onBookingUpdate
}) => {
  const { user } = useAuth();
  const { roles } = useUserRoles();

  // Use passed userRoles or fall back to current user's roles
  const effectiveRoles = userRoles || roles;
  const isAdmin = effectiveRoles.isAdmin;
  const isBookingOwner = booking?.user_id === user?.id;
  const quotesQuery = useQuotes(booking?.id || '');

  // Setup realtime subscription for quotes
  useRealtimeQuotes({
    bookingId: booking?.id || '',
    enabled: true && !!booking?.id
  });
  const onQuoteUpdate = async () => {
    if (booking) {
      console.log('[BOOKING-DETAILS-MODAL] Quote update triggered, invalidating quotes cache');
      quotesQuery.invalidateQuotes();
    }
  };
  const handleStatusUpdate = async () => {
    // No automatic refresh - let realtime subscription handle updates
    console.log('[BOOKING-DETAILS-MODAL] Status update completed - relying on realtime');
  };

  // Log when quotes data changes
  useEffect(() => {
    if (booking && isOpen && quotesQuery.data) {
      console.log('[BOOKING-DETAILS-MODAL] Quotes data updated:', quotesQuery.data.length);
    }
  }, [booking, isOpen, quotesQuery.data]);
  if (!booking) return null;

  // Use quotes directly from React Query - no local state
  const quotes = quotesQuery.data || [];
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-6xl h-[95vh] md:h-[90vh] flex flex-col p-0 bg-background border-border">
        <div className="px-4 md:px-6 pt-4 md:pt-6 pb-4 border-b border-border bg-card/50 flex-shrink-0">
          <BookingDetailsHeader
            booking={booking}
            isAdmin={isAdmin}
            onStatusUpdate={handleStatusUpdate}
            showBackButton={false}
            isInModal={true}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          <BookingDetailsTabs
            booking={booking}
            quotes={quotes}
            onQuoteUpdate={onQuoteUpdate}
            onBookingUpdate={onBookingUpdate}
            isAdmin={isAdmin}
            isBookingOwner={isBookingOwner}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default BookingDetailsModal;